import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { CheckRounded, PersonAddAlt1Outlined } from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  DealUser,
  InternalDeal,
  ProcessingPayload,
  RegistrationStatus,
  SalesStatus,
} from "../../../Models/ambassador";
import { getCurrentUserId } from "../../../Helpers/authHelpers";
import { useProcessDeal } from "../../../Query/useAmbassador";
import { Panel, RegistrationStatusChip } from "../Common/AmbassadorUi";
import { displayName } from "../Common/format";
import {
  SALES_STATUSES,
  useAmbassadorLabels,
} from "../Common/useAmbassadorLabels";

const OPEN = [
  RegistrationStatus.SUBMITTED,
  RegistrationStatus.UNDER_REVIEW,
  RegistrationStatus.QUESTIONS_PENDING,
];

/** Statuses como can move a deal to from its current one; mirrors the
 * backend (questions are asked from the conversation). */
const nextStatuses = (current: RegistrationStatus, isAdmin: boolean) => {
  if (OPEN.includes(current)) {
    return [
      ...(current !== RegistrationStatus.UNDER_REVIEW
        ? [RegistrationStatus.UNDER_REVIEW]
        : []),
      RegistrationStatus.CONFIRMED,
      RegistrationStatus.REJECTED,
    ];
  }
  const decided = [RegistrationStatus.CONFIRMED, RegistrationStatus.REJECTED];
  return isAdmin && decided.includes(current)
    ? [RegistrationStatus.UNDER_REVIEW]
    : [];
};

/** How the ambassador sees a sales status once released. */
const externalSales = (status: SalesStatus): SalesStatus | null => {
  if (status === SalesStatus.OPEN) return null;
  if (status === SalesStatus.NEGOTIATION) return SalesStatus.OFFER_SUBMITTED;
  return status;
};

interface Props {
  deal: InternalDeal;
  owners: DealUser[];
  isAdmin: boolean;
}

/** The decisions on a deal: owner, registration status and sales status.
 * Messages and internal notes are written in their own panels. */
const ProcessingPanel: React.FC<Props> = ({ deal, owners, isAdmin }) => {
  const { t } = useTranslation("ambassador");
  const labels = useAmbassadorLabels();
  const processDeal = useProcessDeal();

  const [ownerId, setOwnerId] = useState<number | null>(deal.owner?.id ?? null);
  const [status, setStatus] = useState<RegistrationStatus>(
    deal.registration_status,
  );
  const [rejectionReason, setRejectionReason] = useState("");
  const [salesStatus, setSalesStatus] = useState<SalesStatus | null>(
    deal.sales_status,
  );
  const [release, setRelease] = useState(true);
  const currentUserId = getCurrentUserId();

  const targets = nextStatuses(deal.registration_status, isAdmin);
  const confirmed = status === RegistrationStatus.CONFIRMED;
  const statusChanged = status !== deal.registration_status;
  const salesChanged =
    confirmed &&
    salesStatus !== null &&
    (salesStatus !== deal.sales_status ||
      (release && salesStatus !== deal.released_sales_status));

  // Owners can only be people with the deal management feature; keep the
  // current one listed even if they lost it.
  const ownerOptions =
    deal.owner && !owners.some((user) => user.id === deal.owner?.id)
      ? [deal.owner, ...owners]
      : owners;

  const payload = (): ProcessingPayload => ({
    ...(ownerId !== (deal.owner?.id ?? null) && { owner_id: ownerId }),
    ...(statusChanged && { registration_status: status }),
    ...(statusChanged &&
      status === RegistrationStatus.REJECTED && {
        rejection_reason: rejectionReason.trim(),
      }),
    ...(salesChanged && {
      sales_status: salesStatus as SalesStatus,
      release_sales_status: release,
    }),
  });
  const hasChanges = Object.keys(payload()).length > 0;
  const missingReason =
    statusChanged &&
    status === RegistrationStatus.REJECTED &&
    !rejectionReason.trim();

  const save = (data: ProcessingPayload) =>
    processDeal.mutate({ dealId: deal.id, data });

  const releaseHint = () => {
    if (salesStatus === null || !salesChanged) return null;
    const shown = externalSales(salesStatus);
    if (shown === null) return t("review.salesHiddenHint");
    if (shown !== salesStatus) {
      return t("review.salesShownAsHint", {
        status: labels.salesStatus(shown),
      });
    }
    return null;
  };

  return (
    <Panel title={t("review.processingTitle")}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 13, color: color.gray500 }}>
            {t("review.currentStatus")}
          </Typography>
          <RegistrationStatusChip status={deal.registration_status} />
        </Box>

        {isAdmin ? (
          <TextField
            select
            size="small"
            label={t("review.owner")}
            value={ownerId ?? ""}
            onChange={(event) =>
              setOwnerId(
                event.target.value === "" ? null : Number(event.target.value),
              )
            }
          >
            <MenuItem value="">{t("inbox.unassigned")}</MenuItem>
            {ownerOptions.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {displayName(user)}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          // Deal managers work on their own deals; an unassigned one becomes
          // theirs with their first step, someone else's they take over.
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 12, color: color.gray500 }}>
                {t("review.owner")}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                {deal.owner?.id === currentUserId
                  ? t("review.ownerYou")
                  : displayName(deal.owner) || t("inbox.unassigned")}
              </Typography>
            </Box>
            {deal.owner?.id !== currentUserId && currentUserId !== null && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<PersonAddAlt1Outlined />}
                disabled={processDeal.isPending}
                onClick={() => save({ owner_id: currentUserId })}
              >
                {t("review.takeOver")}
              </Button>
            )}
          </Box>
        )}
        {!isAdmin && !deal.owner && (
          <Typography sx={{ fontSize: 12, color: color.gray500, mt: -1 }}>
            {t("review.autoOwnerHint")}
          </Typography>
        )}

        {targets.length > 0 && (
          <TextField
            select
            size="small"
            label={t("review.decision")}
            value={status}
            onChange={(event) => setStatus(Number(event.target.value))}
          >
            <MenuItem value={deal.registration_status}>
              {t("review.keepStatus", {
                status: labels.registrationStatus(deal.registration_status),
              })}
            </MenuItem>
            {targets.map((value) => (
              <MenuItem key={value} value={value}>
                {OPEN.includes(deal.registration_status)
                  ? labels.registrationStatus(value)
                  : t("review.reopen")}
              </MenuItem>
            ))}
          </TextField>
        )}

        {statusChanged && status === RegistrationStatus.REJECTED && (
          <TextField
            label={t("review.rejectionReason")}
            helperText={t("review.rejectionReasonHint")}
            value={rejectionReason}
            onChange={(event) => setRejectionReason(event.target.value)}
            required
            multiline
            minRows={2}
            inputProps={{ maxLength: 5000 }}
          />
        )}

        {confirmed && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: "10px",
              border: `1px solid ${color.gray200}`,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <TextField
              select
              size="small"
              label={t("review.salesStatus")}
              value={salesStatus ?? ""}
              onChange={(event) => setSalesStatus(Number(event.target.value))}
            >
              {SALES_STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {labels.salesStatus(value)}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={release}
                  onChange={(event) => setRelease(event.target.checked)}
                />
              }
              label={t("review.releaseSales")}
              sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
            />
            <Typography sx={{ fontSize: 12, color: color.gray500 }}>
              {deal.external_sales_status !== null
                ? t("review.ambassadorSeesSales", {
                    status: labels.salesStatus(deal.external_sales_status),
                  })
                : t("review.ambassadorSeesNoSales")}
              {releaseHint() && ` ${releaseHint()}`}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            startIcon={<CheckRounded />}
            disabled={processDeal.isPending || !hasChanges || missingReason}
            onClick={() => save(payload())}
          >
            {t("review.save")}
          </Button>
        </Box>
      </Box>
    </Panel>
  );
};

export default ProcessingPanel;
